import { ArrayUtils } from '@shopping/common'
import { SelectedOptions } from 'common/ducks/product/option'
import projectConfig from '../config/project'
import { Delivery, Product, ProvidedNotice, TodayDispatch } from 'common/types/entities/api/product'
import { StatusType } from '../constants/product/statusType'
import { axios, isBrowser } from 'common/utils'
import hostConfig from 'common/config'
import { getSubVertical, getSubVerticalPath, InspectionStatus, SubVertical } from 'common/constants'
import { CurationExtensionType } from '../constants/product/curationExtensionType'
import { WindowProduct } from 'common/types/entities/mongo/product'
import { AddressBook } from 'common/types/entities/api/addressBook'
import { isMobileEnv, isPcEnv } from 'common/utils/common'
import { isIe9 } from 'common/utils/oldIeVersion'
import { getDiscountPrice } from '../types/entities/mongo/product'
import { ProductParam } from 'common/types/params'
import { StoreProducts } from 'common/types/entities/store'
import { SSChannel } from 'common/types/entities/api/channel'
import { isValidTrCodeForClickLog, makeNaPmWithTRCode, TRCode } from 'common/services/naverAnalytics'
import { DeliveryLeaveAttributeType } from 'common/constants/product/deliveryLeaveAttributeType'
import { isValidPayUser } from 'common/services/pay'
import { SearchLogeye } from 'common/types/entities/api/logeye'
import {
  ArticleDetailState,
  ContentDisplayLocation,
  ContentsDisplayStatus,
  ProductNotice,
  ProductNoticeContent,
} from 'common/types/entities/news'
import { isAfter, isBefore } from 'tz-date-fns'
import * as queryString from 'query-string'
import { getTRCodeByNaPm } from 'common/services/logeye'
import { isEmptyObject } from 'common/utils/logic/object'

export enum WindowSize {
  WIDTH = 605,
  HEIGHT = 452,
}

export const preCheckForOrder = (
  selectedOptions: SelectedOptions,
  product: Product,
  isGift: boolean,
  isOrderProduct?: boolean,
) => {
  const { purchaseQuantityInfo, name, todayDispatch, productDeliveryInfo, stockQuantity } = product
  const productId = product.id

  if (!selectedOptions.product[productId] || selectedOptions.product[productId].length === 0) {
    alert('옵션을 선택하지 않으셨습니다. 옵션을 선택해 주세요.')
    return false
  }

  const hasSupplementOption = selectedOptions.product[productId].find(option => option.isSupplement)
  if (isGift && hasSupplementOption) {
    alert('추가상품은 선물을 할 수 없습니다.')
    return false
  }

  if (isGift && selectedOptions.product[productId].length !== 1) {
    alert('선물은 옵션을 1개만 선택가능합니다.')
    return false
  }

  const filteredProduct = selectedOptions.product[productId].filter(option => !option.isSupplement)
  const totalCount = filteredProduct.reduce((acc, cur) => {
    return acc + cur.count
  }, 0)

  if (filteredProduct.length === 0) {
    alert('추가상품만 구매할 수 없습니다.')
    return false
  }

  if (totalCount > stockQuantity) {
    alert(`재고가 부족합니다. ${stockQuantity}개 이하로 구매해주세요.`)
    return false
  }

  if (purchaseQuantityInfo) {
    const { minPurchaseQuantity, maxPurchaseQuantityPerId, maxPurchaseQuantityPerOrder } = purchaseQuantityInfo
    if (totalCount < minPurchaseQuantity) {
      alert(
        `${name} 상품은 ${minPurchaseQuantity}개 이상부터 구매하실 수 있습니다. ${minPurchaseQuantity}개 이상 구매해주세요.`,
      )
      return false
    }

    if (totalCount > maxPurchaseQuantityPerId) {
      alert(
        `${name} 상품은 ${maxPurchaseQuantityPerId}개 이하로 구매하실 수 있습니다. ${maxPurchaseQuantityPerId}개 이하로 구매해주세요.`,
      )
      return false
    }

    if (totalCount > maxPurchaseQuantityPerOrder) {
      alert(
        `${name} 상품은 ${maxPurchaseQuantityPerOrder}개 이하로 구매하실 수 있습니다. ${maxPurchaseQuantityPerOrder}개 이하로 구매해주세요.`,
      )
      return false
    }
  }

  if (isOrderProduct && isTodayDispatch(todayDispatch, productDeliveryInfo)) {
    if (totalCount > productDeliveryInfo.todayStockQuantity) {
      return confirm(
        `오늘출발 가능상품보다 요청하신 수량이 많습니다.\n[오늘출발 상품] 잔여 ${
          productDeliveryInfo.todayStockQuantity
        }개\n요청하신 수량으로 주문하시면 일반배송으로 주문됩니다. 계속 주문하시겠습니까?`,
      )
    }
  }

  return true
}

export const isInvalidUri = (product: Product, params: any): boolean => {
  const { windowName, subWindowName, productId, storeId } = params
  const { ssChannel } = product
  if (!ssChannel || !ssChannel.id) {
    return true
  }
  const subVertical = getSubVertical(subWindowName || windowName)
  return (
    ('development|test'.indexOf(process.env.NODE_ENV) === -1 && product.id.toString() !== productId.toString()) ||
    storeId.toString() !== ssChannel.id.toString() ||
    ssChannel.windowSubVertical !== subVertical
  )
}

export const isAvailableProduct = (product: Product): boolean => {
  // if (['development', 'test'].includes(process.env.NODE_ENV)) return true

  const { windowProduct, ssChannel, productStatusType } = product

  if (!windowProduct || windowProduct.inspectionStatus !== InspectionStatus.COMPLETE) return false

  if ([SubVertical.DIRECT_FARM, SubVertical.LOCAL_FOOD].includes(ssChannel.windowSubVertical)) {
    return [StatusType.SALE, StatusType.OUTOFSTOCK, StatusType.SUSPENSION].includes(productStatusType)
  }

  if (
    [
      SubVertical.KIDS,
      SubVertical.HOME_MEAL,
      SubVertical.TRADITIONAL_ALCOHOL,
      SubVertical.PET,
      SubVertical.HOME_LIVING,
    ].includes(ssChannel.windowSubVertical)
  ) {
    return [StatusType.SALE, StatusType.OUTOFSTOCK].includes(productStatusType)
  }
  return windowProduct.exposure
}

export const isAvailableSSProduct = (product: Product): boolean => {
  return (
    product &&
    [StatusType.SALE, StatusType.OUTOFSTOCK].includes(product.productStatusType) &&
    product.channelProductDisplayStatusType === 'ON'
  )
}

export const logProductPageview = async (product: Product, trCode?: string) => {
  product['tr'] = trCode || (window['wcs'] && window['wcs'].getInflowRoute && window['wcs'].getInflowRoute())
  await axios.post(`/v1/products/${product.windowProduct._id}/log`, product, {
    headers: {
      accept: 'application/json',
      'Content-Type': 'application/json',
    },
  })
}

export const increaseProductView = async (id: string) => {
  await axios.post(
    `/v1/products/${id}/view`,
    {},
    {
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json',
      },
    },
  )
}

export const reloadSSProducts = async (productId: string, coordiProductIds: string[]) => {
  const result = await axios.get(`/v1/products/${productId}/coordi`, {
    params: { ids: coordiProductIds },
  })

  return result && result.data
}

export const getProductUrl = (
  product: Product,
  isMobile: boolean,
  includeHost: boolean = true,
  NaPm: string,
  fromOutLink?: boolean,
  shownTab?: boolean,
) => {
  const { ssChannel, id } = product
  const { pc, mobile } = hostConfig.shoppingWebUri
  const config = isMobile ? mobile : pc
  return `${includeHost ? config.host : ''}${getSubVerticalPath(ssChannel.windowSubVertical)}/stores/${
    ssChannel.id
  }/products/${id}?NaPm=${encodeURIComponent(NaPm)}${fromOutLink ? '&type=outlink' : ''}${shownTab ? '#INTRODUCE' : ''}`
}

export const getProductInflowUrl = (productUrl: string, trCode: TRCode, channelType: string, merchantNo?: string) => {
  return `${
    isPcEnv ? hostConfig.naverPayUri.pc : hostConfig.naverPayUri.mobile
  }/inflow/outlink?url=${encodeURIComponent(productUrl)}&tr=${trCode}${
    channelType === '네이버페이' ? `&no=${merchantNo}` : ''
  }`
}

export const getSmartStoreInflowUrl = (productId: string, trCode: TRCode, trxCode?: string) => {
  return `${
    hostConfig.smartStoreUri[process.env.PROJECT_NAME]
  }/inflow/outlink/product?p=${productId}&tr=${trCode}&trx=${trxCode}`
}

export const getCategoryMoreUrl = (product: Product): string => {
  if (!product.displayCategory) {
    return
  }

  const displayCategory = product.displayCategory

  const ids = displayCategory.wholeId
    .split('|')
    .filter(e => !!e)
    .sort((a, b) => (a.length > b.length ? 1 : 0))

  ids.shift()

  const queryParams = ids.map(id => `query=${id}`).join('&')
  const subverticalPath = getSubVerticalPath(product.ssChannel.windowSubVertical)
  const path = subverticalPath.startsWith('/living') ? '/living' : subverticalPath
  return (
    displayCategory.id &&
    `${hostConfig.windowUri[projectConfig.project]}${path}/list/category?${queryParams}&sort=REL_DES`
  )
}

export const fetchProvidedNotices = async (productNo: string): Promise<ProvidedNotice> => {
  const response = await axios.get<ProvidedNotice>(`/v1/products/${productNo}/notices`)
  return response && response.data
}

export const getProductExposureTypes = (
  product: Product,
  queryParams,
): {
  purchase: {
    purchasable: boolean
    offline: boolean
    onlyTalkTalk: boolean
  }
  buttons: {
    buy: boolean
    cart: boolean
    talktalk: boolean
  }
} => {
  const { frm } = queryParams
  const purchasable = product.payExposure && StatusType.SALE === product.productStatusType
  const offline = !product.payExposure
  const onlyTalkTalk = frm !== 'chat' && product.orderRequestUsable && purchasable

  const buy =
    (purchasable && !product.orderRequestUsable) || (purchasable && product.orderRequestUsable && frm === 'chat')
  const cart = buy && (!product.eCoupon || !product.eCoupon.restrictCart)
  const talktalk = product.ssChannel.talkExposureYn

  return {
    purchase: {
      purchasable,
      offline,
      onlyTalkTalk,
    },
    buttons: {
      buy,
      cart,
      talktalk,
    },
  }
}

export const sendRecentlyViewedProduct = async (
  productId: string,
  extensionType: CurationExtensionType,
  extensionKey: string,
  merchantNo: string,
  NaPm?: string,
) => {
  if (!document.cookie.includes('NID_SES') || !document.cookie.includes('NNB')) {
    return
  }

  const params = {
    merchantNo,
    tr: getTRCodeByNaPm(NaPm),
  }
  if (extensionType) params['extensionType'] = extensionType
  if (extensionKey) params['extensionKey'] = extensionKey

  await axios.get(`/v1/products/${productId}/recentlyViewed?${queryString.stringify(params)}`)
}

export const fetchAddressBook = async (storeId: string, addressId: string): Promise<AddressBook> => {
  const addressResponse = await axios.get<AddressBook>(`/v1/channels/${storeId}/addressBooks/${addressId}`)
  return addressResponse && addressResponse.data
}

export const setScrollTab = (tabId: string, setLocationKept?: Function) => {
  if (!location) return

  if (location.search.indexOf(`tab=${tabId}`) > -1 && location.hash === '') {
    if (setLocationKept) setLocationKept()
    const tabbarPosition = document.getElementById('tab').offsetTop
    window.scrollTo(0, tabbarPosition)
    location.replace(`#${tabId.toUpperCase()}`)
  }
}

export const fetchCoordiProducts = async (productIds: string[]): Promise<Product[]> => {
  const response = await axios.post(`/v1/products/all`, {
    ids: productIds,
  })

  const products = response && response.data
  if (!products) return null

  return products.filter(coordiProduct => coordiProduct.productStatusType === StatusType.SALE)
}

export const fetchProducts = async (params: ProductParam): Promise<StoreProducts> => {
  const response = await axios.get(`/v1/products`, {
    params,
  })
  return response && response.data
}

export const historyCheck = () => {
  return (
    isBrowser &&
    typeof window === 'object' &&
    window.history &&
    window.history.state &&
    typeof window.history.state === 'object' &&
    window.history.state.hasOwnProperty('product')
  )
}

export const fetchVideoKey = async (videoId: string) => {
  const response = await axios.get(`/v1/products/videoIds/${videoId}`)
  return response && response.data
}

export const sendClickLog = productId => {
  if (!isBrowser || !productId) return
  const image = new Image()
  const uri = isMobileEnv ? hostConfig.windowUri.mobile : hostConfig.windowUri.pc
  image.src = `${uri}/itemdetail/${productId}/bizclk?d=${Date.now()}`
}

export const sendGiftClickLog = (
  productId: string,
  channelNo: string,
  viewType: string,
  landingUrl: string,
  trCode: TRCode,
) => {
  if (!isBrowser || !productId || !channelNo || !viewType) return
  if (!isValidTrCodeForClickLog(trCode)) return
  const params = {
    viewType,
    landingUrl,
    chnlNo: channelNo,
    pid: productId,
    refinfoPlatform: 'mobile',
    refinfoChannel: 'shopping',
    refinfoClass: 'naver.shopping.service',
    refinfoService: 'service',
    refinfoDetail: 'present',
    tr: trCode,
  }
  const image = new Image()
  image.src = `${hostConfig.giftUri.clickLog}?${queryString.stringify(params)}`
}

export const getPurchaseInfomation = (product: Product) => {
  const { benefitsView, category, ssChannel } = product
  const discountedPrice = getDiscountPrice(product)
  return {
    id: product.id,
    productName: product.name,
    isSupplement: false,
    count: 1,
    price: discountedPrice,
    extraCharge: 0,
    totalPrice: discountedPrice,
    showRemoveButton: true,
    optionTitle: [],
    originPrice: product.windowProduct.salePrice,
    sellerDiscountPrice: isPcEnv
      ? benefitsView.sellerImmediateDiscountAmount
      : benefitsView.mobileSellerImmediateDiscountAmount,
    managerDiscountPrice: isPcEnv
      ? benefitsView.managerImmediateDiscountAmount
      : benefitsView.mobileManagerImmediateDiscountAmount,
    naverPaySellerNo: product.channel.naverPaySellerNo,
    options: [],
    itemName: null,
    largeCategorizeCategoryId: category && category.category1Id,
    middleCategorizeCategoryId: category && category.category2Id,
    smallCategorizeCategoryId: category && category.category3Id,
    detailCategorizeCategoryId: category && category.category4Id,
    largeCategorizeCategoryName: category && category.category1Name,
    middleCategorizeCategoryName: category && category.category2Name,
    smallCategorizeCategoryName: category && category.category3Name,
    detailCategorizeCategoryName: category && category.category4Name,
    naSiteId: ssChannel.naSiteId,
    standardOptionThumbnail: undefined,
    standardOptionColorName: undefined,
    standardOptionColor: undefined,
    standardOptionSize: undefined,
  }
}

export const getProductByIds = async (params: ProductParam) => {
  const response = await axios.get(`/v1/products`, { params })
  return response && response.data
}

export const getRelationProductUrl = (
  channel: SSChannel,
  relationProductId: string,
  isMobile: boolean,
  includeHost: boolean = true,
  fromOutLink?: boolean,
) => {
  const { pc, mobile } = hostConfig.shoppingWebUri
  const config = isMobile ? mobile : pc
  return `${includeHost ? config.host : ''}${getSubVerticalPath(channel.windowSubVertical)}/stores/${
    channel.id
  }/relation-products/${relationProductId}${fromOutLink ? '?type=outlink' : ''}`
}

export const getProductUrlById = (
  id: string,
  ssChannel: SSChannel,
  isMobile: boolean,
  includeHost: boolean = true,
  NaPm: string,
  fromOutLink?: boolean,
  anchoring?: string,
) => {
  const { pc, mobile } = hostConfig.shoppingWebUri
  const config = isMobile ? mobile : pc
  const param = {}
  if (NaPm) param['NaPm'] = NaPm
  if (fromOutLink) param['type'] = 'outlink'
  return `${includeHost ? config.host : ''}${getSubVerticalPath(ssChannel.windowSubVertical)}/stores/${
    ssChannel.id
  }/products/${id}${isEmptyObject(param) ? '' : `?${queryString.stringify(param)}`}${anchoring ? anchoring : ''}`
}

export const fetchWindowProduct = async (productId: string): Promise<WindowProduct> => {
  const response = await axios.get<WindowProduct>(`/v1/products/${productId}/windowProduct`)
  return response && response.data
}

export const isTodayDispatch = (todayDispatch: TodayDispatch, productDeliveryInfo: Delivery): boolean => {
  if (!todayDispatch || !productDeliveryInfo) return false

  if (DeliveryLeaveAttributeType.NORMAL === productDeliveryInfo.deliveryAttributeType) return false

  const { thisDayDispatchYn, possibleDispatchDays } = todayDispatch
  let hasTodayStockQuantity = true
  if ('todayStockQuantity' in productDeliveryInfo) {
    hasTodayStockQuantity = productDeliveryInfo.todayStockQuantity > 0
  }
  if (thisDayDispatchYn) return thisDayDispatchYn && hasTodayStockQuantity

  return ArrayUtils.isNotEmpty(possibleDispatchDays) && hasTodayStockQuantity
}

export const fetchProduct = async (productId: string): Promise<Product> => {
  const response = await axios.get<Product>(`/v1/products/${productId}`)
  return response && response.data
}

export const moveQnaWrite = async () => {
  if (!isBrowser) return

  if (isPcEnv) {
    isIe9() && ((window as any).__disposeQnaMessage__ = this.disposeQnaMessage)
    window.open(
      `${location.pathname}/qna/write?type=qna`,
      `${isIe9() ? '' : 'write-qna'}`,
      `width=${WindowSize.WIDTH},height=${WindowSize.HEIGHT},resizable=0`,
    )
    return
  }
  const isValid = await isValidPayUser()
  if (!isValid) return
  location.href = `${location.pathname}/qna/write?type=qna`
}

export const showPointPlusBanner = (logeye: SearchLogeye, isPointPlusMember: boolean): boolean => {
  const hasNaverOn = logeye && logeye.tracks && logeye.tracks[0] && logeye.tracks[0].naverOn

  if (isPointPlusMember) return hasNaverOn

  // TODO 4월 1일 조건 처리
  const month = new Date().getMonth()
  return month < 3 ? hasNaverOn : false
}

export const fetchProductNotices = async (productId: string, accountNo: number): Promise<ProductNoticeContent> => {
  if (!accountNo || !productId) return null
  const response = await axios.get<ProductNotice>(`/v1/products/${productId}/accounts/${accountNo}/notices`)

  const productNotices = response && response.data
  if (!productNotices) return null

  const { channelNotice, productNotice } = productNotices
  const filteredChannelNotice = getFilteredNotice(channelNotice)
  const filteredProductNotice = getProductNotice(filteredChannelNotice, productNotice)

  return {
    channelNoticeContent: filteredChannelNotice && filteredChannelNotice.editorDetailContentVO,
    productNoticeContent: filteredProductNotice && filteredProductNotice.editorDetailContentVO,
  }
}

const getFilteredNotice = (notice: ArticleDetailState) => {
  if (!notice || !notice.contentsBBSVO || !notice.editorDetailContentVO) return null

  const { contentsBBSVO } = notice
  const {
    contentsDisplayStatusType,
    displayLocationType,
    storePopupDisplayStartDate,
    storePopupDisplayEndDate,
  } = contentsBBSVO

  if (contentsDisplayStatusType !== ContentsDisplayStatus.ON) return null

  const locationTypes = [
    ContentDisplayLocation.ALL,
    isPcEnv ? ContentDisplayLocation.WEB : ContentDisplayLocation.MOBILE,
  ]
  if (!locationTypes.includes(displayLocationType)) return null

  if (storePopupDisplayStartDate && storePopupDisplayEndDate) {
    const date = new Date()
    if (!isAfter(date, storePopupDisplayStartDate) || !isBefore(date, storePopupDisplayEndDate)) return null
  }

  return notice
}

const getProductNotice = (channelNotice: ArticleDetailState, productNotice: ArticleDetailState): ArticleDetailState => {
  const filteredProductNotice = getFilteredNotice(productNotice)
  if (!channelNotice) return filteredProductNotice
  if (!filteredProductNotice) return null

  if (channelNotice.contentsBBSVO.id === filteredProductNotice.contentsBBSVO.id) return null

  return filteredProductNotice
}

export const makeNaPmUrl = (trCode: TRCode, trx?: string) => event => {
  let url = null
  if (!event) return null
  const { currentTarget } = event
  if (currentTarget && currentTarget.tagName && currentTarget.tagName.toUpperCase() === 'A') {
    const params = queryString.parse(currentTarget.search)
    params['NaPm'] = decodeURIComponent(makeNaPmWithTRCode(trCode, null, trx))
    url = `${isIe9() ? '/' : ''}${currentTarget.pathname}?${queryString.stringify(params)}${currentTarget.hash}`
    currentTarget.href = url
    return url
  }
  return url
}
